import React, { useCallback, useMemo } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { DropdownItem } from '@atlaskit/dropdown-menu';
import UploadIcon from '@atlaskit/icon/core/upload';

import { useContentTypeEventService } from '../ContentTypeEventServiceContext.tsx';

const i18n = defineMessages({
	slidesExport: {
		id: 'content-types-header.ellipsis.open.ellipsis.slides.export.non-final',
		defaultMessage: 'Export to PDF',
		description: 'The text shown for the Export to PDF button in the slides overflow menu.',
	},
});

type SlidesExportMenuItemProps = {
	closeParentMenu: () => void;
};

/**
 * Slides-specific Export menu item. A single flat button that dispatches an
 * 'export' command with format 'pdf' via the ContentTypeEventService bridge,
 * which the canvas slides app receives and handles.
 */
export const SlidesExportMenuItem = ({
	closeParentMenu,
}: SlidesExportMenuItemProps): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	// The event service is provided by WhiteboardShell via context.
	// We mount as 'confluence' (the AFM sender key) so commands are dispatched
	// to canvas handlers registered under 'slides'.
	const eventService = useContentTypeEventService();
	const confluenceEventMount = useMemo(
		() => eventService.mountEventService('confluence'),
		[eventService],
	);

	const handleExportClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'slidesExport',
				source: 'overflowMenu',
			},
		}).fire();
		confluenceEventMount.sendCommand({
			type: 'export',
			payload: { product: 'slides', format: 'pdf' },
		});
		closeParentMenu();
	}, [createAnalyticsEvent, confluenceEventMount, closeParentMenu]);

	return (
		<DropdownItem
			onClick={handleExportClick}
			testId="slides-export-button"
			elemBefore={<UploadIcon label="" />}
		>
			<FormattedMessage {...i18n.slidesExport} />
		</DropdownItem>
	);
};
